import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useResourcesList, useResourceMutations } from "../api";
import { ResourceImportModal } from "../ResourceImportModal/ResourceImportModal";
import { buildResourceBatchActions, type ResourceTab } from "./batchActions";
import type { Resource } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ResourceTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useResourceMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } = useResourcesList({
    page: { number: pageNumber, size: pageSize },
    filter: { active: tab === "active" },
  });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<Resource>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "externalName",
      header: "External Name",
      cell: ({ row }) => row.original.externalName,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => row.original.description,
    },
    {
      accessorKey: "default",
      header: "Default",
      cell: ({ row }) =>
        row.original.default ? <FontAwesomeIcon icon={faCheck} /> : "",
    },
  ];

  const runOnSelected = async (
    fn: (r: Resource) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    let failures = 0;
    for (const r of targets) {
      try {
        await fn(r);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: `Failed on ${failures} of ${targets.length} resource(s).`,
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (r) => archive(r.id),
        (n) => `${n} resource(s) archived.`
      );
    } else {
      await runOnSelected(
        (r) => restore(r.id, r.name),
        (n) => `${n} resource(s) restored.`
      );
    }
  };

  const batchActions = buildResourceBatchActions(tab, sel.selectedIds.length, {
    onDelete: () => setConfirmOpen(true),
    onRestore: () => setConfirmOpen(true),
  });

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Resources"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Resource"
        secondaryAction={{
          label: "Import",
          onClick: () => setImportOpen(true),
        }}
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as ResourceTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No resources found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={tab === "active" ? "Archive resources?" : "Restore resources?"}
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} resource(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      <ResourceImportModal
        show={importOpen}
        onClose={() => setImportOpen(false)}
        onError={(text) => {
          setImportOpen(false);
          setToast({ variant: "danger", text });
        }}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
